import { useMemo, useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { api } from '../lib/api'
import { getVacancyBackground, getVacancyIcon } from '../lib/vacancyCategories'
import { getLocalizedField, useSiteLang } from '../lib/localizedContent'
import ResumeApplicationModal from '../components/ResumeApplicationModal'
import { Link } from 'react-router-dom'
import { MapPin, DollarSign, Briefcase, Send, Eye, Search, X } from 'lucide-react'

interface Vacancy {
  id: string
  title: string
  titleEN: string
  titleRU: string
  category?: string
  categoryRU?: string
  categoryEN?: string
  description: string
  descriptionEN: string
  descriptionRU: string
  requirements: string
  requirementsEN: string
  requirementsRU: string
  salary?: string
  salaryRU?: string
  salaryEN?: string
  location?: string
  locationRU?: string
  locationEN?: string
  isActive: boolean
}

type WorkFormatFilter = 'all' | 'remote' | 'office'

const REMOTE_PATTERNS = ['удалённая работа', 'удаленная работа', 'remote work', 'remote']

const isRemoteVacancy = (vacancy: Vacancy) => {
  const values = [vacancy.location, vacancy.locationRU, vacancy.locationEN]
    .filter(Boolean)
    .map((value) => value!.toLowerCase())

  return values.some((value) => REMOTE_PATTERNS.some((pattern) => value.includes(pattern)))
}

const Vacancies = () => {
  const { t } = useTranslation()
  const [vacancies, setVacancies] = useState<Vacancy[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [workFormatFilter, setWorkFormatFilter] = useState<WorkFormatFilter>('all')
  const [applicationVacancy, setApplicationVacancy] = useState<Vacancy | null>(null)

  const ref = useRef(null)
  const lang = useSiteLang()

  useEffect(() => {
    loadVacancies()
  }, [])

  const loadVacancies = async () => {
    try {
      const data = await api.vacancies.getAll()
      setVacancies(data.filter((v: Vacancy) => v.isActive))
    } catch (err) {
      console.error('Error loading vacancies:', err)
      setError('Failed to load vacancies')
    } finally {
      setLoading(false)
    }
  }

  const categoryOptions = useMemo(() => {
    const values = new Set<string>()
    vacancies.forEach((vacancy) => {
      if (vacancy.category) values.add(vacancy.category)
    })
    return Array.from(values).sort()
  }, [vacancies])

  const filteredVacancies = useMemo(() => {
    const query = searchTerm.trim().toLowerCase()

    return vacancies.filter((vacancy) => {
      const title = getLocalizedField(vacancy, 'title', lang).toLowerCase()
      const description = getLocalizedField(vacancy, 'description', lang).toLowerCase()

      const matchesSearch =
        !query ||
        title.includes(query) ||
        description.includes(query)

      const matchesCategory =
        categoryFilter === 'all' || vacancy.category === categoryFilter

      const isRemote = isRemoteVacancy(vacancy)
      const matchesWorkFormat =
        workFormatFilter === 'all' ||
        (workFormatFilter === 'remote' && isRemote) ||
        (workFormatFilter === 'office' && !isRemote)

      return matchesSearch && matchesCategory && matchesWorkFormat
    })
  }, [vacancies, searchTerm, categoryFilter, workFormatFilter, lang])

  const hasActiveFilters =
    searchTerm.trim() !== '' || categoryFilter !== 'all' || workFormatFilter !== 'all'

  const resetFilters = () => {
    setSearchTerm('')
    setCategoryFilter('all')
    setWorkFormatFilter('all')
  }

  if (loading) {
    return (
      <div className="pt-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="pt-16 min-h-screen flex items-center justify-center">
        <div className="text-center text-red-600">
          <p>{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="page-shell">
      <section className="section-padding bg-gradient-to-br from-primary-50 via-white to-accent-50">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('vacancys.title')} <span className="gradient-text">{t('vacancy.title.vacancy')}</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              {t('vacancys.description')}
            </p>
          </motion.div>
        </div>
      </section>

      <section ref={ref} className="section-padding bg-white">
        <div className="container-max">
          {vacancies.length > 0 && (
            <div className="mb-8 rounded-2xl border border-gray-100 bg-gray-50 p-4 md:p-6 min-w-0 overflow-hidden">
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 min-w-0">
                <div className="lg:col-span-2 min-w-0">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('vacancys.search')}
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder={t('vacancys.searchPlaceholder')}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="min-w-0">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('vacancys.filterCategory')}
                  </label>
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="all">{t('vacancys.filterCategoryAll')}</option>
                    {categoryOptions.map((category) => {
                      const sampleVacancy = vacancies.find((vacancy) => vacancy.category === category)
                      const label = sampleVacancy
                        ? getLocalizedField(sampleVacancy, 'category', lang)
                        : category

                      return (
                        <option key={category} value={category}>
                          {label}
                        </option>
                      )
                    })}
                  </select>
                </div>

                <div className="min-w-0">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('vacancys.filterWorkFormat')}
                  </label>
                  <select
                    value={workFormatFilter}
                    onChange={(e) => setWorkFormatFilter(e.target.value as WorkFormatFilter)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="all">{t('vacancys.filterWorkFormatAll')}</option>
                    <option value="remote">{t('vacancys.filterWorkFormatRemote')}</option>
                    <option value="office">{t('vacancys.filterWorkFormatOffice')}</option>
                  </select>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-gray-500">
                  {t('vacancys.resultsCount', { count: filteredVacancies.length })}
                </p>
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-700"
                  >
                    <X className="w-4 h-4 mr-1" />
                    {t('vacancys.resetFilters')}
                  </button>
                )}
              </div>
            </div>
          )}

          {vacancies.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center py-12"
            >
              <Briefcase className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">{t('vacancys.zero')}</p>
              <p className="text-gray-500 mt-2">{t('vacancys.checkBack')}</p>
            </motion.div>
          ) : filteredVacancies.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center py-12"
            >
              <Search className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">{t('vacancys.noResults')}</p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-4 inline-flex items-center text-primary-600 hover:text-primary-700 font-medium"
              >
                <X className="w-4 h-4 mr-1" />
                {t('vacancys.resetFilters')}
              </button>
            </motion.div>
          ) : (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 w-full min-w-0">
              {filteredVacancies.map((vacancy, index) => {
                const categoryKey = vacancy.categoryRU || vacancy.category
                const background = getVacancyBackground(categoryKey)
                const CategoryIcon = getVacancyIcon(categoryKey)

                return (
                  <motion.div
                    key={vacancy.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.6 }}
                    className="relative min-w-0 overflow-hidden rounded-2xl shadow-lg border border-gray-100 card-hover p-6 flex flex-col bg-slate-900"
                    style={{
                      backgroundImage: background.image
                        ? `${background.gradient}, url(${background.image})`
                        : background.gradient,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                  >
                    <div className="absolute inset-0 bg-black/10"></div>
                    <div className="relative z-10 w-12 h-12 bg-white/15 backdrop-blur-sm rounded-lg flex items-center justify-center mb-4">
                      <CategoryIcon className="w-6 h-6 text-white" />
                    </div>

                    {getLocalizedField(vacancy, 'category', lang) && (
                      <p className="relative z-10 text-xs uppercase tracking-[0.2em] text-white/70 mb-3 break-words">
                        {getLocalizedField(vacancy, 'category', lang)}
                      </p>
                    )}

                    <h3 className="relative z-10 text-xl font-semibold text-white mb-3 break-words">
                      {getLocalizedField(vacancy, 'title', lang)}
                    </h3>
                    <p className="relative z-10 text-white/85 mb-4 leading-relaxed line-clamp-3 break-words">
                      {getLocalizedField(vacancy, 'description', lang)}
                    </p>

                    <div className="relative z-10 space-y-2 mb-4">
                      {getLocalizedField(vacancy, 'location', lang) && (
                        <div className="flex items-center text-sm text-white/80">
                          <MapPin className="w-4 h-4 mr-2 text-white flex-shrink-0" />
                          {getLocalizedField(vacancy, 'location', lang)}
                        </div>
                      )}
                      {getLocalizedField(vacancy, 'salary', lang) && (
                        <div className="flex items-center text-sm text-white/80">
                          <DollarSign className="w-4 h-4 mr-2 text-white flex-shrink-0" />
                          {getLocalizedField(vacancy, 'salary', lang)}
                        </div>
                      )}
                    </div>

                    <div className="relative z-10 mt-auto space-y-3">
                      <Link
                        to={`/vacancy/${vacancy.id}`}
                        className="w-full flex items-center justify-center rounded-xl bg-white text-slate-900 hover:bg-white/90 transition-colors px-4 py-3 font-semibold"
                      >
                        {t('vacancys.view')}
                        <Eye className="w-4 h-4 ml-2" />
                      </Link>

                      <button
                        type="button"
                        onClick={() => setApplicationVacancy(vacancy)}
                        className="w-full flex items-center justify-center bg-primary-600 rounded-xl text-white hover:bg-primary-700 transition-colors px-4 py-3 font-semibold border border-white/30"
                      >
                        {t('vacancys.apply')}
                        <Send className="w-4 h-4 ml-2" />
                      </button>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          )}
        </div>
      </section>

      <ResumeApplicationModal
        isOpen={!!applicationVacancy}
        onClose={() => setApplicationVacancy(null)}
        vacancyId={applicationVacancy?.id || ''}
        vacancyTitle={
          applicationVacancy
            ? getLocalizedField(applicationVacancy, 'title', lang)
            : ''
        }
      />
    </div>
  )
}

export default Vacancies
